import { c as _c } from "react/compiler-runtime";
import type { MergeabilityStatus } from '@/lib/hooks/mergeability-status/get-mergeability-status.server';
import type { PrV2CheckRunResponse, PrV2ReviewResponse } from '@/lib/pr-v2/types';
import type { Reviewer } from '@/types/github';
import { ChevronRight, Copy } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import type { PrV2MetadataSummary } from '../shared/pr-v2-metadata';
import { selectGeneralReviewRunForPromptCopy, selectLatestCubicReviewPrompt, shouldShowGeneralReviewPromptCopyAction } from '../comments/pr-v2-review-prompt-utils';
import { PrV2DemoStatus } from '../demo/pr-v2-demo-status';
import { PrLabels } from '../header/pr-v2-labels';
import { PrV2FaviconState } from '../pr-v2-favicon-state';
import { PrV2Checks } from '../shared/pr-v2-checks';
import { SIDEBAR_HEADING_CLASS } from '../shared/sidebar-heading-classes';
import { CubicReviewRunButton } from './cubic-review-run-button';
import { CubicReviewStatus } from './cubic-review-status';
import { AsanaIssueComponent } from './integrations/asana/issue';
import { DeferredIssueTrackers } from './integrations/deferred-issue-trackers';
import { GithubIssueComponent } from './integrations/github/issue';
import { JiraIssueComponent } from './integrations/jira/issue';
import { LinearIssueComponent } from './integrations/linear/issue';
import { AiAttribution } from './pr-v2-ai-attribution/ai-attribution';
import { toCubicReviewActivity } from './pr-v2-auto-fix-loop-model';
import { PrV2AutoFixLoopRow } from './pr-v2-auto-fix-loop-row';
import { PrV2EditLabelsButton } from './pr-v2-edit-labels-button';
import { PrV2EditReviewersButton } from './pr-v2-edit-reviewers-button';
import { buildPrV2Labels, deriveCubicRunStatus, isCubicReviewer, toGithubReviewer } from './pr-v2-sidebar-model';
import { RequestedTeamRow } from './requested-team-row';
import { Logo } from '@/components/icons';
import { DismissReviewDialog } from '@/components/reviewers/dismiss-review-dialog';
import { ReviewerItem } from '@/components/reviewers/reviewer-item';
import { Button } from '@/components/ui/button';
import { BetterTooltip } from '@/components/ui/tooltip';
import { useGitHubReviews } from '@/data/reviews/use-github-reviews';
import { useReviewRuns } from '@/data/reviews/use-review-statuses';
import { usePrV2ReviewerActions } from '@/data/v2/pr/use-pr-v2-reviewer-actions';
import { useIsMobile } from '@/hooks/use-mobile';
import { trackClientEvent } from '@/lib/analytics/posthog-client';
import { useMergeabilityStatus } from '@/lib/hooks/mergeability-status/use-mergeability-status';
import { buildPrV2Reviewers } from '@/lib/pr-v2/reviewers';
import { dispatchPrV2TabHandoff } from '@/lib/pr-v2/tab-handoff';
import { PR_V2_TABS } from '@/lib/pr-v2/tabs';
import { ReviewerState } from '@/types/github';
import { copyToClipboard } from '@/utils/clipboard';
type PrV2SidebarProps = {
  owner: string;
  repo: string;
  number: string;
  headSha: string | null;
  authorLogin: string | null;
  installationId: number | null;
  metadata: PrV2MetadataSummary;
  reviews: PrV2ReviewResponse[];
  checks: PrV2CheckRunResponse[];
  initialMergeabilityStatus?: MergeabilityStatus | null;
  checksLoading?: boolean;
  checksError?: string | null;
};
type SidebarReviewer = {
  login: string;
  avatarUrl: string | null;
  state: (typeof ReviewerState)[keyof typeof ReviewerState];
  reviewId: number | null;
};
const REVIEWER_SIDEBAR_ICON_CLASS = 'size-4';
// Match the size-6 icon buttons so gap-2 is even between glyph centers.
const REVIEWER_ACTION_SLOT_CLASS = 'flex size-6 shrink-0 items-center justify-center';
const CUBIC_REVIEWER_LABEL = 'cubic';
function CubicReviewerAvatar() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <span data-testid="pr-v2-cubic-reviewer-avatar" aria-hidden="true" className="inline-flex size-5 shrink-0 items-center justify-center"><Logo size={16} /></span>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function buildReviewers(metadata: PrV2MetadataSummary, reviews: PrV2ReviewResponse[]): SidebarReviewer[] {
  return buildPrV2Reviewers(metadata.reviewers, reviews).map(reviewer => ({
    login: reviewer.login,
    avatarUrl: reviewer.avatarUrl,
    state: reviewer.state,
    reviewId: reviewer.reviewId == null ? null : Number(reviewer.reviewId)
  }));
}
export function PrV2Sidebar(t0) {
  const $ = _c(152);
  const {
    owner,
    repo,
    number,
    headSha,
    authorLogin,
    installationId,
    metadata,
    reviews,
    checks,
    initialMergeabilityStatus: t1,
    checksLoading: t2,
    checksError: t3
  } = t0;
  const initialMergeabilityStatus = t1 === undefined ? null : t1;
  const checksLoading = t2 === undefined ? false : t2;
  const checksError = t3 === undefined ? null : t3;
  const isMobile = useIsMobile();
  const [isLabelsExpanded, setIsLabelsExpanded] = useState(!isMobile);
  const [prevIsMobile, setPrevIsMobile] = useState(isMobile);
  const reviewerActions = usePrV2ReviewerActions(owner, repo, number);
  let t4;
  if ($[0] !== initialMergeabilityStatus) {
    t4 = {
      initialData: initialMergeabilityStatus
    };
    $[0] = initialMergeabilityStatus;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const {
    status: mergeabilityStatus
  } = useMergeabilityStatus(owner, repo, number, t4);
  const {
    reviewRuns,
    isLoading: isReviewRunsLoading
  } = useReviewRuns(owner, repo, number);
  const {
    reviews: githubReviews
  } = useGitHubReviews(owner, repo, number);
  const [dismissReviewer, setDismissReviewer] = useState(null);
  const [isDismissDialogOpen, setIsDismissDialogOpen] = useState(false);
  let t5;
  if ($[2] !== reviewRuns) {
    t5 = deriveCubicRunStatus(reviewRuns);
    $[2] = reviewRuns;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const cubicRunStatus = t5;
  let latestGeneralReviewPrompt;
  let t6;
  if ($[4] !== githubReviews || $[5] !== reviewRuns) {
    const generalReviewRun = selectGeneralReviewRunForPromptCopy(reviewRuns);
    latestGeneralReviewPrompt = selectLatestCubicReviewPrompt(githubReviews, generalReviewRun);
    t6 = shouldShowGeneralReviewPromptCopyAction(generalReviewRun, latestGeneralReviewPrompt);
    $[4] = githubReviews;
    $[5] = reviewRuns;
    $[6] = latestGeneralReviewPrompt;
    $[7] = t6;
  } else {
    latestGeneralReviewPrompt = $[6];
    t6 = $[7];
  }
  const shouldShowCubicPromptCopyIcon = t6;
  let handleDismissReview;
  let labels;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t7;
  let t8;
  let t9;
  if ($[8] !== authorLogin || $[9] !== checks || $[10] !== cubicRunStatus || $[11] !== dismissReviewer || $[12] !== headSha || $[13] !== installationId || $[14] !== isMobile || $[15] !== isReviewRunsLoading || $[16] !== latestGeneralReviewPrompt || $[17] !== mergeabilityStatus || $[18] !== metadata || $[19] !== number || $[20] !== owner || $[21] !== prevIsMobile || $[22] !== repo || $[23] !== reviewerActions || $[24] !== reviews || $[25] !== shouldShowCubicPromptCopyIcon) {
    const rawReviewers = buildReviewers(metadata, reviews);
    const otherReviewers = rawReviewers.filter(_temp);
    let t15;
    if ($[36] !== metadata) {
      t15 = buildPrV2Labels(metadata);
      $[36] = metadata;
      $[37] = t15;
    } else {
      t15 = $[37];
    }
    labels = t15;
    const canDismissReview = _temp2;
    const canReRequestReview = _temp3;
    let t16;
    if ($[38] !== dismissReviewer || $[39] !== reviewerActions) {
      t16 = async reason => {
        if (!dismissReviewer?.reviewId || !reason.trim()) {
          return;
        }
        const reviewId = Number(dismissReviewer.reviewId);
        if (!Number.isFinite(reviewId) || reviewId <= 0) {
          return;
        }
        const reviewerLogin = dismissReviewer.login;
        ;
        try {
          await reviewerActions.dismissReview({
            reviewId,
            message: reason.trim()
          });
          setIsDismissDialogOpen(false);
          setDismissReviewer(null);
          toast.success(`Dismissed review by ${reviewerLogin}`);
        } catch (t17) {
          const error = t17;
          toast.error(error instanceof Error ? error.message : "Failed to dismiss review");
        }
      };
      $[38] = dismissReviewer;
      $[39] = reviewerActions;
      $[40] = t16;
    } else {
      t16 = $[40];
    }
    handleDismissReview = t16;
    let t17;
    if ($[41] !== reviewerActions) {
      t17 = async reviewer_2 => {
        if (reviewerActions.isMutating) {
          return;
        }
        ;
        try {
          await reviewerActions.reRequestReview(reviewer_2.login);
          toast.success(`Re-requested review from ${reviewer_2.login}`);
        } catch (t18) {
          const error_0 = t18;
          toast.error(error_0 instanceof Error ? error_0.message : "Failed to re-request review");
        }
      };
      $[41] = reviewerActions;
      $[42] = t17;
    } else {
      t17 = $[42];
    }
    const handleReRequestReview = t17;
    let t18;
    if ($[43] !== reviewerActions) {
      t18 = reviewer_3 => {
        if (reviewerActions.isMutating || !reviewer_3.reviewId) {
          return;
        }
        setDismissReviewer(reviewer_3);
        setIsDismissDialogOpen(true);
      };
      $[43] = reviewerActions;
      $[44] = t18;
    } else {
      t18 = $[44];
    }
    const handleSelectDismissReviewer = t18;
    let t19;
    if ($[45] !== latestGeneralReviewPrompt) {
      t19 = () => {
        if (!latestGeneralReviewPrompt) {
          return;
        }
        copyToClipboard(latestGeneralReviewPrompt, {
          successMessage: "Copied prompt for AI agents",
          errorMessage: "Failed to copy prompt"
        });
      };
      $[45] = latestGeneralReviewPrompt;
      $[46] = t19;
    } else {
      t19 = $[46];
    }
    const handleCopyGeneralReviewPrompt = t19;
    let t20;
    if ($[47] !== installationId || $[48] !== number || $[49] !== owner || $[50] !== repo) {
      t20 = reviewer_4 => {
        if (!reviewer_4.reviewId) {
          return;
        }
        const isCommentNavigation = reviewer_4.state === ReviewerState.COMMENTED;
        trackClientEvent(isCommentNavigation ? "v2_pr_navigate_to_comment" : "v2_pr_navigate_to_rejection", installationId?.toString(), {
          owner,
          repo,
          pr_number: number,
          reviewer_login: reviewer_4.login,
          review_state: reviewer_4.state
        });
        toast.info(isCommentNavigation ? "Navigating to comment..." : "Navigating to rejection reason...");
        const payload = {
          tab: PR_V2_TABS.DISCUSSION,
          commentId: String(reviewer_4.reviewId)
        };
        requestAnimationFrame(() => {
          dispatchPrV2TabHandoff(payload);
        });
      };
      $[47] = installationId;
      $[48] = number;
      $[49] = owner;
      $[50] = repo;
      $[51] = t20;
    } else {
      t20 = $[51];
    }
    const handleReviewerStateIconClick = t20;
    if (prevIsMobile !== isMobile) {
      setPrevIsMobile(isMobile);
      setIsLabelsExpanded(!isMobile);
    }
    t13 = "pb-3 pl-0 md:pb-0";
    const t21 = cubicRunStatus.status === "running";
    if ($[52] !== checks || $[53] !== headSha || $[54] !== mergeabilityStatus || $[55] !== t21) {
      t14 = <PrV2FaviconState checks={checks} currentHeadSha={headSha} mergeabilityStatus={mergeabilityStatus} hasActiveCubicReview={t21} />;
      $[52] = checks;
      $[53] = headSha;
      $[54] = mergeabilityStatus;
      $[55] = t21;
      $[56] = t14;
    } else {
      t14 = $[56];
    }
    t11 = "w-full md:w-[220px]";
    let t22;
    if ($[57] === Symbol.for("react.memo_cache_sentinel")) {
      t22 = <h3 className={`mb-3 ${SIDEBAR_HEADING_CLASS}`}>Reviewers</h3>;
      $[57] = t22;
    } else {
      t22 = $[57];
    }
    const t23 = <div className="flex shrink-0 items-center"><PrV2EditReviewersButton owner={owner} repo={repo} number={number} reviewers={rawReviewers} requestedTeams={metadata.requestedTeams} authorLogin={authorLogin} reviewerActions={reviewerActions} /></div>;
    if ($[58] !== t23) {
      t12 = <div className="flex w-full flex-row items-start justify-between md:w-[220px]">{t22}{t23}</div>;
      $[58] = t23;
      $[59] = t12;
    } else {
      t12 = $[59];
    }
    t7 = "w-full space-y-2 pr-0.5";
    let t24;
    if ($[60] === Symbol.for("react.memo_cache_sentinel")) {
      t24 = <div className="flex min-w-0 flex-1 items-center gap-2 text-zinc-300"><CubicReviewerAvatar /><span className="text-sm">{CUBIC_REVIEWER_LABEL}</span></div>;
      $[60] = t24;
    } else {
      t24 = $[60];
    }
    let t25;
    if ($[61] !== cubicRunStatus || $[62] !== installationId || $[63] !== number || $[64] !== owner || $[65] !== repo) {
      t25 = <CubicReviewRunButton owner={owner} repo={repo} number={number} installationId={installationId} status={cubicRunStatus} iconClassName={REVIEWER_SIDEBAR_ICON_CLASS} />;
      $[61] = cubicRunStatus;
      $[62] = installationId;
      $[63] = number;
      $[64] = owner;
      $[65] = repo;
      $[66] = t25;
    } else {
      t25 = $[66];
    }
    let t26;
    if ($[67] !== handleCopyGeneralReviewPrompt || $[68] !== shouldShowCubicPromptCopyIcon) {
      t26 = shouldShowCubicPromptCopyIcon ? <BetterTooltip content="Copy prompt for AI agents"><Button type="button" variant="ghost" size="icon" onClick={handleCopyGeneralReviewPrompt} className="size-6 text-zinc-500 hover:text-zinc-300" aria-label="Copy prompt for AI agents"><Copy className={REVIEWER_SIDEBAR_ICON_CLASS} /></Button></BetterTooltip> : null;
      $[67] = handleCopyGeneralReviewPrompt;
      $[68] = shouldShowCubicPromptCopyIcon;
      $[69] = t26;
    } else {
      t26 = $[69];
    }
    let t27;
    if ($[70] !== cubicRunStatus || $[71] !== isReviewRunsLoading) {
      t27 = <CubicReviewStatus status={cubicRunStatus} isLoading={isReviewRunsLoading} iconSlotClassName={`${REVIEWER_ACTION_SLOT_CLASS} cursor-pointer text-zinc-500`} tooltipSide="left" />;
      $[70] = cubicRunStatus;
      $[71] = isReviewRunsLoading;
      $[72] = t27;
    } else {
      t27 = $[72];
    }
    if ($[73] !== t25 || $[74] !== t26 || $[75] !== t27) {
      t8 = <div className="group flex items-center justify-between transition-opacity" data-testid="pr-v2-cubic-reviewer-row">{t24}<div className="flex shrink-0 items-center gap-2 pl-2 pr-0.5">{t25}{t26}{t27}</div></div>;
      $[73] = t25;
      $[74] = t26;
      $[75] = t27;
      $[76] = t8;
    } else {
      t8 = $[76];
    }
    const t28 = cubicRunStatus.status === "running";
    let t29;
    if ($[77] !== isReviewRunsLoading || $[78] !== t28) {
      t29 = toCubicReviewActivity({
        isLoading: isReviewRunsLoading,
        running: t28
      });
      $[77] = isReviewRunsLoading;
      $[78] = t28;
      $[79] = t29;
    } else {
      t29 = $[79];
    }
    if ($[80] !== number || $[81] !== owner || $[82] !== repo || $[83] !== t29) {
      t9 = <PrV2AutoFixLoopRow owner={owner} repo={repo} number={number} cubicReview={t29} />;
      $[80] = number;
      $[81] = owner;
      $[82] = repo;
      $[83] = t29;
      $[84] = t9;
    } else {
      t9 = $[84];
    }
    let t30;
    if ($[85] !== handleReRequestReview || $[86] !== handleReviewerStateIconClick || $[87] !== handleSelectDismissReviewer) {
      t30 = reviewer_5 => <ReviewerItem key={reviewer_5.login} reviewer={toGithubReviewer(reviewer_5)} onReRequest={handleReRequestReview} onDismiss={handleSelectDismissReviewer} canDismiss={canDismissReview(reviewer_5)} canReRequest={canReRequestReview(reviewer_5)} onStateIconClick={handleReviewerStateIconClick} actionIconClassName="size-4" stateIconClassName="[&_svg]:size-4" />;
      $[85] = handleReRequestReview;
      $[86] = handleReviewerStateIconClick;
      $[87] = handleSelectDismissReviewer;
      $[88] = t30;
    } else {
      t30 = $[88];
    }
    t10 = otherReviewers.map(t30);
    $[8] = authorLogin;
    $[9] = checks;
    $[10] = cubicRunStatus;
    $[11] = dismissReviewer;
    $[12] = headSha;
    $[13] = installationId;
    $[14] = isMobile;
    $[15] = isReviewRunsLoading;
    $[16] = latestGeneralReviewPrompt;
    $[17] = mergeabilityStatus;
    $[18] = metadata;
    $[19] = number;
    $[20] = owner;
    $[21] = prevIsMobile;
    $[22] = repo;
    $[23] = reviewerActions;
    $[24] = reviews;
    $[25] = shouldShowCubicPromptCopyIcon;
    $[26] = handleDismissReview;
    $[27] = labels;
    $[28] = t10;
    $[29] = t11;
    $[30] = t12;
    $[31] = t13;
    $[32] = t14;
    $[33] = t7;
    $[34] = t8;
    $[35] = t9;
  } else {
    handleDismissReview = $[26];
    labels = $[27];
    t10 = $[28];
    t11 = $[29];
    t12 = $[30];
    t13 = $[31];
    t14 = $[32];
    t7 = $[33];
    t8 = $[34];
    t9 = $[35];
  }
  let t15;
  if ($[89] !== metadata.requestedTeams) {
    t15 = metadata.requestedTeams.map(_temp4);
    $[89] = metadata.requestedTeams;
    $[90] = t15;
  } else {
    t15 = $[90];
  }
  let t16;
  if ($[91] !== t10 || $[92] !== t15 || $[93] !== t7 || $[94] !== t8 || $[95] !== t9) {
    t16 = <div className={t7}>{t8}{t9}{t10}{t15}</div>;
    $[91] = t10;
    $[92] = t15;
    $[93] = t7;
    $[94] = t8;
    $[95] = t9;
    $[96] = t16;
  } else {
    t16 = $[96];
  }
  let t17;
  if ($[97] !== t11 || $[98] !== t12 || $[99] !== t16) {
    t17 = <section className={t11}>{t12}{t16}</section>;
    $[97] = t11;
    $[98] = t12;
    $[99] = t16;
    $[100] = t17;
  } else {
    t17 = $[100];
  }
  let t18;
  if ($[101] !== headSha || $[102] !== installationId || $[103] !== number || $[104] !== owner || $[105] !== repo) {
    t18 = <AiAttribution owner={owner} repo={repo} number={number} headSha={headSha} installationId={installationId} />;
    $[101] = headSha;
    $[102] = installationId;
    $[103] = number;
    $[104] = owner;
    $[105] = repo;
    $[106] = t18;
  } else {
    t18 = $[106];
  }
  let t19;
  if ($[107] !== checks || $[108] !== checksError || $[109] !== checksLoading || $[110] !== headSha || $[111] !== mergeabilityStatus) {
    t19 = <PrV2Checks checks={checks} headSha={headSha} mergeabilityStatus={mergeabilityStatus} checksLoading={checksLoading} checksError={checksError} className="mt-6 w-full md:w-[220px]" />;
    $[107] = checks;
    $[108] = checksError;
    $[109] = checksLoading;
    $[110] = headSha;
    $[111] = mergeabilityStatus;
    $[112] = t19;
  } else {
    t19 = $[112];
  }
  let t20;
  if ($[113] !== installationId || $[114] !== number || $[115] !== owner || $[116] !== repo) {
    t20 = <PrV2DemoStatus owner={owner} repo={repo} number={number} installationId={installationId} className="mt-6 w-full md:w-[220px]" />;
    $[113] = installationId;
    $[114] = number;
    $[115] = owner;
    $[116] = repo;
    $[117] = t20;
  } else {
    t20 = $[117];
  }
  let t21;
  if ($[118] !== isLabelsExpanded || $[119] !== isMobile || $[120] !== labels.length) {
    t21 = isMobile && labels.length > 0 ? <button type="button" className="group/labels flex items-center gap-1.5" onClick={() => setIsLabelsExpanded(_temp5)} aria-expanded={isLabelsExpanded} aria-label={isLabelsExpanded ? "Collapse labels" : "Expand labels"} data-testid="pr-v2-sidebar-labels-toggle"><span className={SIDEBAR_HEADING_CLASS}>Labels</span><ChevronRight className={`size-4 text-zinc-500 opacity-0 transition-all group-hover/labels:opacity-100 ${isLabelsExpanded ? "rotate-90" : ""}`} /></button> : <h3 className={SIDEBAR_HEADING_CLASS}>Labels</h3>;
    $[118] = isLabelsExpanded;
    $[119] = isMobile;
    $[120] = labels.length;
    $[121] = t21;
  } else {
    t21 = $[121];
  }
  let t22;
  if ($[122] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = () => setIsLabelsExpanded(true);
    $[122] = t22;
  } else {
    t22 = $[122];
  }
  let t23;
  if ($[123] !== installationId || $[124] !== metadata.labels || $[125] !== number || $[126] !== owner || $[127] !== repo) {
    t23 = <PrV2EditLabelsButton owner={owner} repo={repo} number={number} installationId={installationId} labels={metadata.labels} onUpdate={t22} />;
    $[123] = installationId;
    $[124] = metadata.labels;
    $[125] = number;
    $[126] = owner;
    $[127] = repo;
    $[128] = t23;
  } else {
    t23 = $[128];
  }
  let t24;
  if ($[129] !== t21 || $[130] !== t23) {
    t24 = <div className="mb-1.5 flex w-full items-center justify-between gap-3">{t21}{t23}</div>;
    $[129] = t21;
    $[130] = t23;
    $[131] = t24;
  } else {
    t24 = $[131];
  }
  let t25;
  if ($[132] !== isLabelsExpanded || $[133] !== labels) {
    t25 = labels.length > 0 && isLabelsExpanded ? <PrLabels labels={labels} /> : null;
    $[132] = isLabelsExpanded;
    $[133] = labels;
    $[134] = t25;
  } else {
    t25 = $[134];
  }
  let t26;
  if ($[135] !== t24 || $[136] !== t25) {
    t26 = <section className="mt-6 w-full md:w-[220px]">{t24}{t25}</section>;
    $[135] = t24;
    $[136] = t25;
    $[137] = t26;
  } else {
    t26 = $[137];
  }
  let t27;
  if ($[138] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <DeferredIssueTrackers><LinearIssueComponent /><JiraIssueComponent /><AsanaIssueComponent /><GithubIssueComponent /></DeferredIssueTrackers>;
    $[138] = t27;
  } else {
    t27 = $[138];
  }
  let t28;
  if ($[139] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = open => {
      setIsDismissDialogOpen(open);
      if (!open) {
        setDismissReviewer(null);
      }
    };
    $[139] = t28;
  } else {
    t28 = $[139];
  }
  let t29;
  if ($[140] !== handleDismissReview || $[141] !== isDismissDialogOpen) {
    t29 = <DismissReviewDialog isOpen={isDismissDialogOpen} onOpenChange={t28} onDismiss={handleDismissReview} />;
    $[140] = handleDismissReview;
    $[141] = isDismissDialogOpen;
    $[142] = t29;
  } else {
    t29 = $[142];
  }
  let t30;
  if ($[143] !== t13 || $[144] !== t14 || $[145] !== t17 || $[146] !== t18 || $[147] !== t19 || $[148] !== t20 || $[149] !== t26 || $[150] !== t29) {
    t30 = <aside className={t13}>{t14}{t17}{t18}{t19}{t20}{t26}{t27}{t29}</aside>;
    $[143] = t13;
    $[144] = t14;
    $[145] = t17;
    $[146] = t18;
    $[147] = t19;
    $[148] = t20;
    $[149] = t26;
    $[150] = t29;
    $[151] = t30;
  } else {
    t30 = $[151];
  }
  return t30;
}
function _temp5(current) {
  return !current;
}
function _temp4(team) {
  return <RequestedTeamRow key={team.slug ?? team.name} team={team} />;
}
function _temp3(reviewer_1) {
  return reviewer_1.state === ReviewerState.DISMISSED || reviewer_1.state === ReviewerState.CHANGES_REQUESTED;
}
function _temp2(reviewer_0) {
  return (reviewer_0.state === ReviewerState.APPROVED || reviewer_0.state === ReviewerState.CHANGES_REQUESTED) && reviewer_0.reviewId !== null;
}
function _temp(reviewer) {
  return !isCubicReviewer(reviewer.login);
}